/* eslint-disable react-hooks/exhaustive-deps, @typescript-eslint/no-explicit-any, @typescript-eslint/strict-boolean-expressions */

import {
  alpha,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import useIsMobile from 'hooks/useIsMobile';
import {
  ProductType,
  ScannerID,
  EquityFieldKey,
  LegacyEquity,
  SynthOIEquity,
} from '../../types';
import {
  DataGridPremium,
  GridCallbackDetails,
  gridClasses,
  GridColDef,
  GridColumnGroup,
  GridColumnOrderChangeParams,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridPinnedColumnFields,
  GridPinnedRowsProp,
  GridRowParams,
  GridSlots,
  GridSortModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  MuiEvent,
  useGridApiContext,
} from '@spotgamma/x-data-grid-premium';
import { useRecoilValue } from 'recoil';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import { watchlistsState } from 'states';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { EmptyGridOverlay } from './EmptyGridOverlay';
import { EmptyResultsOverlay } from './EmptyResultsOverlay';
import useSetSym from 'hooks/hiro/useSetSym';
import { scannerUnmodifiableFields } from 'config';
import { SGTooltip } from 'components/core';
import { dayjs, getUpdatedColumnOrder } from 'util/shared';
import ScannerInfoSection from './ScannerInfoSection';
import { SORT_FOR_SCANNER_MAP } from 'util/equityhub';

interface CustomToolbarProps {
  setFilterButtonEl: React.Dispatch<
    React.SetStateAction<HTMLButtonElement | null>
  >;
  activeScanner: ScannerID | undefined;
  useDefaultColumns: boolean;
  setUseDefaultColumns: (v: boolean | ((prev: boolean) => boolean)) => void;
  activeWatchlistIds: number[];
  defaultColumnOrder: string[];
  columnOrder: string[];
  setColumnOrder: (order: string[]) => void;
  customSettings?: ReactNode;
  customScannerSettings?: ReactNode;
  showScannerInfo?: boolean;
  compact?: boolean;
}

const CustomToolbar = ({
  setFilterButtonEl,
  activeScanner,
  useDefaultColumns,
  setUseDefaultColumns,
  activeWatchlistIds,
  defaultColumnOrder,
  columnOrder,
  setColumnOrder,
  customSettings,
  customScannerSettings,
  showScannerInfo,
  compact,
}: CustomToolbarProps) => {
  const apiRef = useGridApiContext();
  const theme = useTheme();
  const isMobileQuery = useIsMobile();
  const isMobile = isMobileQuery || !!compact;
  const watchlists = useRecoilValue(watchlistsState);
  const activeWatchlists =
    watchlists?.filter((w) => activeWatchlistIds.includes(w.id as number)) ??
    [];

  const sxProps: SxProps<Theme> = {
    textTransform: 'capitalize',
    fontSize: {
      xs: 10,
      md: 14,
    },
  };

  const slotProps = {
    button: {
      sx: sxProps,
      PopperProps: {
        placement: 'top-end',
      },
    },
  };

  const onResetColumnOrder = () => setColumnOrder(defaultColumnOrder);

  const { getSym } = useSetSym();

  const fileName = `${
    getSym(ProductType.EQUITYHUB) ? `${getSym(ProductType.EQUITYHUB)}_` : ''
  }data-table_${dayjs().format('YYYY-MM-DD')}`;

  // Can automatically filter the grid based on the input, including Symbols
  const quickFilterComponent = (
    <GridToolbarQuickFilter
      size="small"
      variant="outlined"
      color="primary"
      InputProps={{
        sx: {
          width: isMobile ? '100%' : 'auto',
          height: 31,
          '& fieldset': {
            borderColor: alpha(theme.palette.primary.main, 0.55),
            borderWidth: 2,
          },
        },
      }}
    />
  );

  return (
    <GridToolbarContainer
      sx={{
        paddingY: '8px',
        paddingX: '10px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 2,
      }}
    >
      {isMobile ? (
        <Stack direction="column" alignItems="stretch" gap={1.5} width="100%">
          {/* First row: Quick filter */}
          <Box width="100%">{quickFilterComponent}</Box>

          {/* Second row: Main toolbar buttons */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}
            width="100%"
          >
            <Stack direction="row" alignItems="center" gap={1}>
              <GridToolbarFilterButton
                slotProps={slotProps}
                ref={setFilterButtonEl}
              />
              <GridToolbarColumnsButton slotProps={slotProps} />
              <GridToolbarExport
                slotProps={{
                  button: {
                    sx: sxProps,
                  },
                }}
                csvOptions={{
                  getRowsToExport: () => apiRef.current.getAllRowIds(),
                  fileName,
                }}
                excelOptions={{
                  fileName,
                }}
                printOptions={{ disableToolbarButton: true }}
              />
            </Stack>

            <Stack direction="row" alignItems="center" gap={1}>
              <SGTooltip title="Restore Default Column Order">
                <IconButton
                  size="small"
                  sx={{
                    color:
                      columnOrder === defaultColumnOrder
                        ? theme.palette.gray
                        : theme.palette.primary.main,
                  }}
                  onClick={onResetColumnOrder}
                >
                  <SettingsBackupRestoreIcon fontSize="small" />
                </IconButton>
              </SGTooltip>
              {customSettings}
            </Stack>
          </Stack>

          {/* Third row: Scanner settings and checkbox */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}
            width="100%"
          >
            {customScannerSettings}
            {activeScanner && (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    color="primary"
                    checked={useDefaultColumns}
                    onClick={() => setUseDefaultColumns((prev) => !prev)}
                  />
                }
                label={
                  <Typography
                    sx={{
                      textTransform: 'capitalize',
                      fontSize: 10,
                      color: theme.palette.primary.main,
                    }}
                  >
                    Use Default Columns
                  </Typography>
                }
              />
            )}
          </Stack>

          {/* Fourth row: Watchlist info */}
          {activeWatchlists.length > 0 && (
            <Typography
              sx={{
                color: theme.palette.text.secondary,
                fontSize: 10,
                textAlign: 'center',
                width: '100%',
              }}
            >
              Limited to watchlists:{' '}
              {activeWatchlists.map((w) => w.name).join(', ')}
            </Typography>
          )}
        </Stack>
      ) : (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          width="100%"
        >
          <Stack direction="row" alignItems="center" gap={2}>
            {quickFilterComponent}
            {customScannerSettings}
          </Stack>

          <Stack direction="row" alignItems="center" gap={2}>
            <GridToolbarFilterButton
              slotProps={slotProps}
              ref={setFilterButtonEl}
            />
            <GridToolbarColumnsButton slotProps={slotProps} />

            <SGTooltip title="Restore Default Column Order">
              <IconButton
                sx={{
                  color:
                    columnOrder === defaultColumnOrder
                      ? theme.palette.gray
                      : theme.palette.primary.main,
                }}
                onClick={onResetColumnOrder}
              >
                <SettingsBackupRestoreIcon />
              </IconButton>
            </SGTooltip>
            {activeScanner && (
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={useDefaultColumns}
                    onClick={() => setUseDefaultColumns((prev) => !prev)}
                  />
                }
                label={
                  <Typography
                    sx={{
                      textTransform: 'capitalize',
                      fontSize: {
                        xs: 12,
                        sm: 13,
                      },
                      color: theme.palette.primary.main,
                    }}
                  >
                    Use Default Columns
                  </Typography>
                }
              />
            )}
            <GridToolbarExport
              slotProps={{
                button: {
                  sx: sxProps,
                },
              }}
              csvOptions={{
                getRowsToExport: () => apiRef.current.getAllRowIds(),
                fileName,
              }}
              excelOptions={{
                fileName,
              }}
              printOptions={{ disableToolbarButton: true }}
            />

            {customSettings}
          </Stack>
        </Stack>
      )}
      {showScannerInfo && (
        <ScannerInfoSection
          activeScanner={activeScanner}
          activeWatchlistsIds={activeWatchlistIds}
        />
      )}
    </GridToolbarContainer>
  );
};

interface EquityScannerGridProps {
  columns: GridColDef[];
  columnGroups: GridColumnGroup[];
  data: (LegacyEquity | SynthOIEquity)[];
  activeScanner: ScannerID | undefined;
  activeWatchlistIds: number[];
  useDefaultColumns: boolean;
  setUseDefaultColumns: React.Dispatch<React.SetStateAction<boolean>>;
  columnVisibilityModel: GridColumnVisibilityModel;
  setColumnVisibilityModel: React.Dispatch<
    React.SetStateAction<GridColumnVisibilityModel>
  >;
  filterModel: GridFilterModel | undefined;
  setFilterModel: React.Dispatch<
    React.SetStateAction<GridFilterModel | undefined>
  >;
  columnOrder: EquityFieldKey[];
  setColumnOrder: React.Dispatch<React.SetStateAction<EquityFieldKey[]>>;
  defaultColumnOrder: string[];
  loading?: boolean;
  onRowClick?: (
    params: GridRowParams,
    event: MuiEvent,
    details: GridCallbackDetails,
  ) => void;
  customSettings?: ReactNode;
  customScannerSettings?: ReactNode;
  pinnedColumns: GridPinnedColumnFields;
  setPinnedColumns: React.Dispatch<
    React.SetStateAction<GridPinnedColumnFields>
  >;
  sortModel: GridSortModel;
  setSortModel: React.Dispatch<React.SetStateAction<GridSortModel>>;
  showScannerInfo?: boolean;
  currentSym?: string;
  compact?: boolean;
}

export const EquityScannerGrid = ({
  data,
  columns,
  columnGroups,
  activeScanner,
  activeWatchlistIds,
  useDefaultColumns,
  setUseDefaultColumns,
  columnVisibilityModel,
  setColumnVisibilityModel,
  columnOrder,
  setColumnOrder,
  defaultColumnOrder,
  sortModel,
  setSortModel,
  pinnedColumns,
  setPinnedColumns,
  filterModel,
  setFilterModel,
  customSettings,
  customScannerSettings,
  onRowClick,
  loading,
  showScannerInfo,
  currentSym,
  compact,
}: EquityScannerGridProps) => {
  const theme = useTheme();
  const isMobile = useIsMobile();

  const [filterButtonEl, setFilterButtonEl] =
    useState<HTMLButtonElement | null>(null);

  const defaultContentColor = theme.palette.getContrastText(
    theme.palette.background.default,
  );

  const hasFiltersApplied = filterModel?.items && filterModel?.items.length > 0;

  const pinnedRow = useMemo(
    () =>
      data.find(
        (eq: LegacyEquity | SynthOIEquity) =>
          currentSym && eq.sym === currentSym.toUpperCase(),
      ),
    [data, currentSym],
  );

  const rows = useMemo(() => {
    return hasFiltersApplied
      ? data
      : data.filter(
          (eq: LegacyEquity | SynthOIEquity) => eq.sym !== pinnedRow?.sym,
        );
  }, [data, pinnedRow, hasFiltersApplied]);

  const pinnedRowsProp: GridPinnedRowsProp =
    pinnedRow && !hasFiltersApplied ? { top: [pinnedRow] } : {};

  const getTogglableColumns = (columns: GridColDef[]) => {
    return columns
      .filter(
        (column) =>
          !scannerUnmodifiableFields.includes(column.field as EquityFieldKey),
      )
      .map((column) => column.field);
  };

  const orderedColumns = useMemo(() => {
    const orderMap = new Map(columnOrder.map((field, index) => [field, index]));
    const fallback = new Map(
      defaultColumnOrder.map((field, idx) => [field, idx + columns.length]),
    );

    return [...columns].sort(
      (a, b) =>
        (orderMap.get(a.field as EquityFieldKey) ?? fallback.get(a.field)!) -
        (orderMap.get(b.field as EquityFieldKey) ?? fallback.get(b.field)!),
    );
  }, [columnOrder, columns, defaultColumnOrder]);

  const handleColumnOrderChange = (
    params: GridColumnOrderChangeParams,
    _event: any,
    _details: any,
  ) => {
    const newOrder = getUpdatedColumnOrder(orderedColumns, params);
    setColumnOrder(newOrder as EquityFieldKey[]);
  };

  useEffect(() => {
    // Enforce sort model when scanner changes if available
    const requiredSort = activeScanner
      ? SORT_FOR_SCANNER_MAP.get(activeScanner)
      : null;
    if (requiredSort != null) {
      setSortModel([{ field: requiredSort.field, sort: requiredSort.sort }]);
    }
  }, [activeScanner]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
      }}
    >
      <DataGridPremium
        loading={loading}
        rows={rows}
        pinnedRows={pinnedRowsProp}
        columns={orderedColumns}
        onColumnOrderChange={handleColumnOrderChange}
        sortModel={sortModel}
        onSortModelChange={(model) => setSortModel(model)}
        pinnedColumns={pinnedColumns}
        onPinnedColumnsChange={(cols) => setPinnedColumns(cols)}
        columnGroupingModel={columnGroups}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(newModel) => {
          setColumnVisibilityModel(newModel);
          setUseDefaultColumns(false);
        }}
        disableMultipleRowSelection
        disableAggregation
        disableRowGrouping
        pagination={isMobile}
        pageSizeOptions={[5, 10, 25, 50, 100]}
        onRowClick={onRowClick}
        columnHeaderHeight={isMobile ? 70 : 60}
        rowHeight={55}
        density="compact"
        filterModel={filterModel}
        onFilterModelChange={(model: GridFilterModel) => setFilterModel(model)}
        slots={{
          toolbar: CustomToolbar as GridSlots['toolbar'],
          noRowsOverlay: EmptyGridOverlay,
          noResultsOverlay: EmptyResultsOverlay,
        }}
        slotProps={{
          columnsManagement: {
            getTogglableColumns,
          },
          basePopper: {
            sx: {
              '& .MuiSvgIcon-root': {
                color: alpha(defaultContentColor, 0.75),
              },
            },
          },
          panel: {
            anchorEl: filterButtonEl,
            placement: isMobile ? 'bottom-start' : 'bottom-end',
            slotProps: {
              root: {
                style: {
                  width: isMobile ? '90%' : '550px',
                },
              },
            },
          },
          toolbar: {
            setFilterButtonEl,
            activeScanner,
            useDefaultColumns,
            setUseDefaultColumns,
            activeWatchlistIds,
            columnOrder,
            setColumnOrder,
            defaultColumnOrder,
            customSettings,
            customScannerSettings,
            showScannerInfo,
            compact,
          },
          filterPanel: {
            filterFormProps: {
              // Customize inputs by passing props
              logicOperatorInputProps: {
                variant: 'outlined',
                size: 'small',
              },
              columnInputProps: {
                variant: 'outlined',
                size: 'small',
                sx: { mt: 'auto' },
              },
              operatorInputProps: {
                variant: 'outlined',
                size: 'small',
                sx: { mt: 'auto' },
              },
              valueInputProps: {
                InputComponentProps: {
                  variant: 'outlined',
                  size: 'small',
                },
              },
            },
            sx: {
              gap: 2,

              '& .MuiButtonBase-root': {
                textTransform: 'capitalize',
              },
              '& .MuiDataGrid-filterForm': { p: 3, mt: 6 },
              '& .MuiDataGrid-filterFormLogicOperatorInput': { mr: 2 },
              '& .MuiDataGrid-filterFormColumnInput': { mr: 2 },
              '& .MuiDataGrid-filterFormOperatorInput': { mr: 2 },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.primary.main, 0.5),
              },
            },
          },
        }}
        sx={{
          borderRadius: 2,
          outline: 'none',
          backgroundColor: theme.palette.background.default,
          border: 'none',
          '& .hideRightSeparator > .MuiDataGrid-columnSeparator': {
            display: 'none',
          },
          '& .MuiDataGrid-filler': {
            display: 'none',
          },
          '& .MuiDataGrid-row.Mui-selected': {
            backgroundColor: theme.palette.scannerGrid.pinnedRow,
          },
          '& .MuiDataGrid-pinnedRows': {
            '& .MuiDataGrid-row': {
              '& .MuiDataGrid-cell': {
                backgroundColor: theme.palette.scannerGrid.pinnedRow,
              },
            },
          },
          '& .MuiDataGrid-pinnedColumns': {
            backgroundColor: theme.palette.scannerGrid.pinnedColumn,
          },
          '& .MuiDataGrid-row': {
            cursor: 'pointer', // Makes the cursor a hand sign when hovering over rows
          },
          '& .MuiDataGrid-row:nth-of-type(odd):not(.Mui-selected):not(:hover)':
            {
              backgroundColor: alpha(theme.palette.text.primary, 0.05),
            },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: alpha(theme.palette.text.primary, 0.1),
          },
          '& .custom-group-header': {
            '& .MuiDataGrid-columnHeaderTitleContainer': {
              justifyContent: 'center',
              height: '35px',
            },
          },
          '& .grid-header-cell': {
            backgroundColor: theme.palette.scannerGrid.mainHeader,
            ':hover': {
              backgroundColor: alpha(theme.palette.text.primary, 0.1),
            },
            border: 'none',
          },
          '& .MuiDataGrid-topContainer': {
            border: 'none',
          },
          '& .MuiDataGrid-columnHeader': {
            border: 'none',
          },
          '& .MuiDataGrid-columnHeaders': {
            border: 'none',
          },
          '& .MuiDataGrid-columnHeaderTitleContainer': {
            border: 'none',
          },
          '& .MuiDataGrid-scrollbar': {
            background: theme.palette.background.default,
          },
          '& .MuiDataGrid-sortIcon': {
            color: defaultContentColor,
          },
          '& .MuiDataGrid-menuIconButton': {
            color: defaultContentColor,
          },
          '& .MuiDataGrid-filterIcon': {
            color: defaultContentColor,
          },
          '& .MuiDataGrid-menuIconButton:hover': {
            backgroundColor: theme.palette.background.paper,
            color: alpha(defaultContentColor, 0.5),
          },
          '& .MuiDataGrid-iconSeparator': {
            display: 'none',
          },
          '& .MuiDataGrid-footerContainer': {
            justifyContent: 'flex-start',
          },
          '& .MuiDataGrid-cell': {
            color: defaultContentColor,
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiDataGrid-row .MuiDataGrid-cell': {
            border: 'none',
          },
          [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]:
            {
              outline: 'none',
            },
          [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
            {
              outline: 'none',
            },
          '--DataGrid-overlayHeight': '300px',
        }}
      />
    </Box>
  );
};
